// webtop's database in this browser (IndexedDB `webtop`), with a store per thing kept: ICONS, the icon cache
// (client/lib/icons.js), WEBTOPS, the webtops stored locally, and ASSETS, their assets (both the storage applet's: it opens
// this database as it is, never upgrading it, applets/storage/src/idb.js, so their stores are made here), APPLETS, the applets installed in this browser, and APPLET_DATA, what those applets keep (both
// client/lib/applets.js). A new store goes in with DB_VERSION raised by one and created in onupgradeneeded below, next to
// these. Every failure (no IndexedDB, a private window, a full disk) reads as "not kept": get() answers undefined,
// has() false, put() and remove() false, keys() nothing, so the page still works, only nothing survives it.
const DB_NAME = 'webtop';
// Version 2 (2026-10): icons are kept by host, so the entries kept by /icons/... path before were dropped (each is
// fetched again once, under its host). Version 3 (2026-10-29): the `webtops` store. Version 4 (2026-10-31): `assets`.
// Version 5 (2026-10-31): `applets` and `applet-data`.
const DB_VERSION = 5;
export const ICONS = 'icons';
export const WEBTOPS = 'webtops';
export const ASSETS = 'assets';
export const APPLETS = 'applets';
export const APPLET_DATA = 'applet-data';

let db;
function open() {
  db ??= new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
        const stores = req.result.objectStoreNames;
        if (!stores.contains(ICONS)) req.result.createObjectStore(ICONS);
        else if (e.oldVersion < 2) req.transaction.objectStore(ICONS).clear();
        if (!stores.contains(WEBTOPS)) req.result.createObjectStore(WEBTOPS);
        if (!stores.contains(ASSETS)) req.result.createObjectStore(ASSETS);
        if (!stores.contains(APPLETS)) req.result.createObjectStore(APPLETS);
        if (!stores.contains(APPLET_DATA)) req.result.createObjectStore(APPLET_DATA);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
  return db;
}

/** The value kept under `key` in `store`, or undefined. Never rejects. */
export async function get(store, key) {
  const d = await open();
  if (!d) return undefined;
  return new Promise((resolve) => {
    try {
      const req = d.transaction(store).objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(undefined);
    } catch { resolve(undefined); }
  });
}
/** Whether `store` keeps something under `key`, without reading it (a record may be megabytes). Never rejects. */
export async function has(store, key) {
  const d = await open();
  if (!d) return false;
  return new Promise((resolve) => {
    try {
      const req = d.transaction(store).objectStore(store).getKey(key);
      req.onsuccess = () => resolve(req.result !== undefined);
      req.onerror = () => resolve(false);
    } catch { resolve(false); }
  });
}
/** Every key of `store`, in order. Never rejects. */
export async function keys(store) {
  const d = await open();
  if (!d) return [];
  return new Promise((resolve) => {
    try {
      const req = d.transaction(store).objectStore(store).getAllKeys();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve([]);
    } catch { resolve([]); }
  });
}
// A write, resolved once the transaction is done: true when it went through, false when it was refused (a full disk,
// a store that is gone). Never rejects.
function write(store, fn) {
  return open().then((d) => {
    if (!d) return false;
    return new Promise((resolve) => {
      try {
        const tx = d.transaction(store, 'readwrite');
        fn(tx.objectStore(store));
        tx.oncomplete = () => resolve(true);
        tx.onabort = () => resolve(false);
        tx.onerror = (e) => { e.preventDefault(); resolve(false); };
      } catch { resolve(false); }
    });
  });
}
/** Keep `value` under `key` in `store`; true once it is kept. Never rejects. */
export const put = (store, key, value) => write(store, (s) => s.put(value, key));
/** Drop every one of `keys` from `store`; true once they are gone. Never rejects. */
export const remove = (store, keys) => write(store, (s) => { for (const k of keys) s.delete(k); });
